import { useState, useEffect } from 'react';

interface VideoPlayerProps {
  blob: Blob | null; // The video blob
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ blob }) => {
  const [videoUrl, setVideoUrl] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  // Blobs downloaded from older stitched files may be typed
  // application/octet-stream (backend now stores video/mp4). Fall back to
  // video/mp4 so the <source> always carries a playable MIME type.
  const videoType = blob?.type?.startsWith('video/') ? blob.type : 'video/mp4';

  useEffect(() => {
    if (blob) {
      if (blob.size === 0) {
        setError('Downloaded video is empty (0 bytes). Please try publishing again.');
        return;
      }
      const objectUrl = URL.createObjectURL(blob);
      setVideoUrl(objectUrl);
      setError(undefined);

      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }
  }, [blob]);

  if (error) {
    return <div className="video-player-error">{error}</div>;
  }

  return (
    <>
      {!videoUrl ? (
        <p>Loading video...</p>
      ) : (
        <div className="video-player-shell">
          <video
            controls
            controlsList="nodownload"
            className="video-player"
            preload="metadata"
            onError={(event) => {
              const mediaError = (event.currentTarget as HTMLVideoElement).error;
              const code = mediaError?.code;
              // 1=aborted, 2=network, 3=decode, 4=unsupported/format
              const detail =
                code === 3
                  ? 'the file appears to be corrupt or unplayable'
                  : code === 4
                    ? 'the format is not supported by this browser'
                    : code === 2
                      ? 'a network error interrupted loading'
                      : 'playback failed';
              setError(
                `Video failed to load (${detail}, code ${code ?? 'unknown'}, ` +
                  `${blob?.size ?? 0} bytes). Please try publishing again.`
              );
            }}
          >
            <source src={videoUrl} type={videoType} />
            Your browser does not support the video tag.
          </video>
        </div>
      )}
    </>
  );
};

export default VideoPlayer;
